'use client';

import type React from 'react';

import HistoryTable from '@/components/FinalVersion/HistoryTable';
import IndirectTable from '@/components/FinalVersion/IndirectTable';
import OriginalTable from '@/components/FinalVersion/OriginalTable';
import UnifiedTable from '@/components/FinalVersion/UnifiedTable';
import { Box, Paper, Tab, Tabs, Typography, useTheme } from '@mui/material';

import type { Collaborator } from '@/app/models/collaborator';
import type { Project } from '@/app/models/project';
import { useSearchPersonQuery } from '@/app/services/personApi';
import { useSearchProjectsQuery } from '@/app/services/projectApi';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import PageTitle from '@/components/common/pageTitle';
import PeriodDetails from '@/components/periodDetails/PeriodDetails';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ProjectTransfer() {
  const theme = useTheme();
  const rowsPageOptions: number[] = [25, 50, 100, 150, 200];

  const { t } = useTranslation('finalVersion');
  const [tabValue, setTabValue] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [originProject, setOriginProject] = useState<Project | null>(null);
  const [originProjectSearch, setOriginProjectSearch] = useState('');
  const { data: originProjects, isFetching: isLoadingOrigin } = useSearchProjectsQuery({
    searchTerm: originProjectSearch,
    bringClosed: false,
    page: 0,
    size: 10,
    sortKey: 'name',
    sortDirection: 'ASC',
  });
  const [destinationProject, setDestinationProject] = useState<Project | null>(null);
  const [destinationProjectSearch, setDestinationProjectSearch] = useState('');
  const { data: destinationProjects, isFetching: isLoadingDestination } = useSearchProjectsQuery({
    searchTerm: destinationProjectSearch,
    bringClosed: false,
    page: 0,
    size: 10,
    sortKey: 'name',
    sortDirection: 'ASC',
  });
  const [collaborator, setCollaborator] = useState<Collaborator | null>(null);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const { data: collaborators, isFetching: isLoadingCollaborators } = useSearchPersonQuery(
    {
      searchTerm: collaboratorSearch,
      available: true,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: collaboratorSearch.length < 1 },
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const renderTableComponent = () => {
    const filterProps = {
      originProject,
      destinationProject,
      collaborator,
      rowsPerPage,
      setRowsPerPage,
      rowsPageOptions,
    };

    switch (tabValue) {
      case 0:
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <IndirectTable {...filterProps} />;
      case 1:
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <UnifiedTable {...filterProps} />;
      case 2:
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <HistoryTable {...filterProps} />;
      case 3:
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <OriginalTable {...filterProps} />;
      default:
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <IndirectTable {...filterProps} />;
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mr: 2,
      }}
    >
      <PeriodDetails />
      <PageTitle title={t('sharings')} titleLabel={` (${t('final.version')})`} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('origin.project')}
          </Typography>
          <GenericAutoComplete
            value={originProject}
            onChange={(value) => {
              setOriginProject(value);
            }}
            equalityTest={(option, value) => option?.code === value?.code}
            getOptionLabel={(option) =>
              `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
            }
            onSearch={(value) => {
              setOriginProjectSearch(value);
            }}
            options={originProjectSearch !== '' && originProjects?.content ? originProjects?.content : []}
            placeholder={t('search.project') || ''}
            loading={isLoadingOrigin}
            sx={{ width: theme.spacing(50) }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('destination.project')}
          </Typography>
          <GenericAutoComplete
            value={destinationProject}
            onChange={(value) => {
              setDestinationProject(value);
            }}
            equalityTest={(option, value) => option?.code === value?.code}
            getOptionLabel={(option) =>
              `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
            }
            onSearch={(value) => {
              setDestinationProjectSearch(value);
            }}
            options={
              destinationProjectSearch !== '' && destinationProjects?.content ? destinationProjects?.content : []
            }
            placeholder={t('search.project') || ''}
            loading={isLoadingDestination}
            sx={{ width: theme.spacing(50) }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('collaborator')}
          </Typography>
          <GenericAutoComplete
            value={collaborator}
            onChange={(value) => {
              setCollaborator(value);
            }}
            equalityTest={(option, value) => option?.email === value?.email}
            getOptionLabel={(option) => `${option?.badge} - ${option?.name}`}
            onSearch={(value) => {
              setCollaboratorSearch(value);
            }}
            options={collaboratorSearch !== '' && collaborators?.content ? collaborators?.content : []}
            placeholder={t('search.collaborator') || ''}
            loading={isLoadingCollaborators}
            sx={{ width: theme.spacing(50) }}
          />
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="secondary" textColor="secondary">
          <Tab sx={{ textTransform: 'none' }} label={t('indirect')} />
          <Tab sx={{ textTransform: 'none' }} label={t('unified')} />
          <Tab sx={{ textTransform: 'none' }} label={t('history')} />
          <Tab sx={{ textTransform: 'none' }} label={t('original')} />
        </Tabs>
      </Paper>

      {renderTableComponent()}
    </Box>
  );
}
